import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Col,
  Container,
  Form,
  Row,
  Table,
  Button,
  Nav,
  Modal,
  Badge,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import TemplatePreview from "../whatsapp_template/TemplatePreview";
import moment from "moment";

import { ToastContainer, toast } from "react-toastify"; // npm i react-toastify --force
import "react-toastify/dist/ReactToastify.css";
import CountryCode from "../../constants/CountryCode.json";

const CampaignTracking = ({ socket, selectedWhatsAppSetting, userInfo }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [receivedMessage, setReceivedMessage] = useState();
  const [isSpinner, setIsSpinner] = useState(false);
  const [campaignData, setCampaignData] = useState(
    location.state ? location.state : {},
  );
  const [history, setHistory] = useState([]);
  const [template, setTemplateData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("total");
  const [showModal, setShowModal] = useState(false);
  const docxIcon = "/user_images/doc-icon.png";
  const canExport =
    userInfo?.userrole !== "USER" || userInfo?.can_export_leads === true;
  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);
  useEffect(() => {
    socket?.on("receivedwhatsappmessage", (data) => {
      setReceivedMessage(data);
    });
  }, [socket]);
  useEffect(() => {
    handleStatusClick("total");
    if (!campaignData?.campaign_id) return;

    if (campaignData.template_name) {
      getTemplateName(campaignData.template_name, selectedWhatsAppSetting);
    }
  }, [
    campaignData?.campaign_id,
    campaignData?.template_name,
    selectedWhatsAppSetting,
  ]);

  const formatPhoneNumber = (number) => {
    const shouldHideNumber =
      userInfo?.userrole === "USER" &&
      userInfo?.is_whatsapp_number_hidden === true;

    if (shouldHideNumber && number) {
      const numberString = String(number);
      if (numberString.length <= 5) return numberString;
      const visibleDigits = numberString.slice(-5);
      const maskedDigits = "X".repeat(numberString.length - 5);
      return `${maskedDigits}${visibleDigits}`;
    }
    return number;
  };
  const getTemplateName = async (name, selectedWhatsAppSetting) => {
    const result = await WhatsAppAPI.getTemplateByName(
      name,
      selectedWhatsAppSetting,
    );
    if (result.data && result.data.length > 0) {
      setTemplateData(result.data[0]);
    } else {
      setTemplateData([]);
    }
  };

  const handleStatusClick = async (status) => {
    setSelectedStatus(status);
    setIsSpinner(true);
    try {
      const res = await WhatsAppAPI.getMsgHistoryByStatus(
        campaignData?.campaign_id,
        status,
      );

      if (res.success && Array.isArray(res.records)) {
        const formatted = res.records.map((item) => ({
          ...item,
          number: formatPhoneNumber(item.number), // Apply phone number formatting
          message:
            item.message?.length > 100
              ? `${item.message.substring(0, 100)}...`
              : item.message,
        }));
        // console.log("Formatted Records: ", formatted);

        setFiltered(res.records);
      } else {
        // If success is false or records are not an array
        setFiltered([]);
      }
    } catch (err) {
      // console.error("Failed to fetch filtered status records", err);
      setFiltered([]);
    }
    setIsSpinner(false);
  };

  // const labels = { beforeSelect: " " };
  const header = [
    { title: "Name", prop: "name", isFilterable: true },
    {
      title: "Phone",
      prop: "number",
      isFilterable: true,
      cell: (row) => formatPhoneNumber(row.number),
    },
    { title: "Delivery Status", prop: "delivery_status", isFilterable: true },
    ...(selectedStatus === "failed"
      ? [{ title: "Error Msg", prop: "err_message", isFilterable: true }]
      : []),
  ];

  const handleDownload = () => {
    if (!canExport) {
      toast.error("You do not have permission to export.");
      return;
    }

    if (filtered.length > 0) {
      downloadCSV(filtered);
      toast.success("File download successfully.");
    } else {
      toast.error("No data available to download");
    }
  };
  const downloadCSV = (data) => {
    const csv = convertToCSV(data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "campaign_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const extractCountryData = (number) => {
    if (!number) return { localNumber: "", code: "" };

    // Find matching country code
    const match = CountryCode.find((c) => number.startsWith(c.country_code));

    if (!match) {
      return {
        localNumber: number, // No split
        code: "",
      };
    }

    // Remove code from number → local number only
    const localNumber = number.replace(match.country_code, "");

    return {
      localNumber,
      code: match.country_code,
    };
  };

  const convertToCSV = (data) => {
    const header = ["Name", "CountryCode", "Number"];

    const rows = data.map((item) => {
      const extracted = extractCountryData(item.number);

      // Apply your formatting (masking logic) on LOCAL NUMBER
      const formattedLocalNumber = formatPhoneNumber(extracted.localNumber);

      return [item.name, extracted.code, formattedLocalNumber];
    });

    return [header.join(","), ...rows.map((row) => row.join(","))].join("\n");
  };

  const handleBack = () => {
    navigate("/campaign");
  };
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "success";
      case "in progress":
        return "warning";
      case "failed":
        return "danger";
      default:
        return "secondary";
    }
  };
  const renderFormattedMessage = () => {
    const bodyComponent = template?.components?.find((c) => c.type === "BODY");
    if (!bodyComponent?.text) return null;

    let formattedText = bodyComponent.text;
    const bodyTextParams = campaignData?.body_text_params || {};

    // Replace parameters like {{1}} with actual values
    formattedText = formattedText.replace(/{{(\d+)}}/g, (match, key) => {
      return bodyTextParams[key] || match;
    });

    // Apply text formatting
    formattedText = formattedText
      .replace(/```([\s\S]*?)```/g, "<pre>$1</pre>") // Multiline code block
      .replace(/\*(.*?)\*/g, "<b>$1</b>") // Bold
      .replace(/_(.*?)_/g, "<i>$1</i>") // Italic
      .replace(/~(.*?)~/g, "<s>$1</s>") // Strikethrough
      .replace(/\n+/g, "<br />"); // Line breaks

    return <span dangerouslySetInnerHTML={{ __html: formattedText }} />;
  };

  const getHeader = () => {
    const headerComponent = template?.components?.find(
      (c) => c.type === "HEADER",
    );

    // Decide media URL
    let selectedFileURL = "";
    if (campaignData.params_file_id) {
      selectedFileURL = `/public/${userInfo.tenantcode}/attachment/${campaignData.params_file_title}`;
    } else {
      selectedFileURL = headerComponent?.example?.header_handle?.[0] || "";
    }

    if (!selectedFileURL) return null;

    // Determine media type
    const format = headerComponent?.format?.toUpperCase() || "";

    if (format.includes("IMAGE")) {
      return (
        <img
          src={selectedFileURL}
          alt="Header Preview"
          className="img-fluid rounded w-100 mb-2"
          style={{ height: "200px", objectFit: "cover" }}
        />
      );
    } else if (format.includes("VIDEO")) {
      return (
        <video
          src={selectedFileURL}
          controls
          className="img-fluid rounded w-100 mb-2"
          style={{ height: "200px", objectFit: "cover" }}
        />
      );
    } else if (format.includes("DOCUMENT")) {
      return (
        <a href={selectedFileURL} target="_blank" rel="noopener noreferrer">
          <img
            src={docxIcon}
            alt="Document Preview"
            className="img-fluid rounded w-100 mb-2"
            style={{ height: "200px", objectFit: "cover" }}
          />
        </a>
      );
    }

    return null;
  };
  const getFooter = () => {
    const footerComponent = template?.components?.find(
      (c) => c.type === "FOOTER",
    );
    return footerComponent?.text ? (
      <div className="template-footer text-muted">{footerComponent.text}</div>
    ) : null;
  };

  const getButtons = () => {
    const buttonComponent = template?.components?.find(
      (c) => c.type === "BUTTONS",
    );
    if (!buttonComponent?.buttons?.length) return null;

    return (
      <div className="template-buttons mt-3">
        {buttonComponent.buttons.map((btn, index) => {
          const key = `${btn.type}-${btn.text}-${index}`;

          if (btn.type === "URL") {
            return (
              <Badge
                key={key}
                bg="light"
                text="primary"
                className="mb-2"
                style={{
                  padding: "10px 40px",
                  fontSize: "0.9rem",
                  borderRadius: "0.5rem",
                  display: "block",
                  textAlign: "center",
                }}
              >
                <i className="fa fa-external-link me-2"></i>
                {btn.text}
              </Badge>
            );
          } else if (btn.type === "PHONE_NUMBER") {
            return (
              <Badge
                key={key}
                bg="light"
                text="primary"
                className="mb-2"
                style={{
                  padding: "10px 40px",
                  fontSize: "0.9rem",
                  borderRadius: "0.5rem",
                  display: "block",
                  textAlign: "center",
                }}
              >
                <i className="fa fa-phone me-2"></i>
                {btn.text}
              </Badge>
            );
          } else {
            return (
              <Badge
                key={key}
                bg="light"
                text="primary"
                className="mb-2"
                style={{
                  padding: "10px 40px",
                  fontSize: "0.9rem",
                  borderRadius: "0.5rem",
                  display: "block",
                  textAlign: "center",
                }}
              >
                <i className="fa fa-hand-pointer-o me-2"></i>
                {btn.text}
              </Badge>
            );
          }
        })}
      </div>
    );
  };

  return (
    <>
      <Container className="mt-5">
        {/* Header */}
        <Row className="mx-5 mb-4 align-items-center">
          <Col>
            <Card className="p-4 shadow-sm border-0 rounded-3 header-bg">
              <Row>
                <Col lg={8}>
                  <h4 className="fw-bold mb-1">📢 Campaign Overview</h4>
                  {/* <h5 className="text-primary">{campaignData?.campaign_name}</h5> */}
                </Col>
                <Col lg={4} className="text-end">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleBack}
                  >
                    <i className="bi bi-arrow-left me-1" /> Back to Campaigns
                  </Button>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Campaign Summary */}
        <Row className="mx-5 mb-4">
          <Col>
            <Card className="p-4 bg-white shadow-sm border-0 rounded-3">
              <h6 className="mb-4 text-secondary">📊 Campaign Summary</h6>
              <Row className="gy-4">
                <Col md={3}>
                  <small className="text-muted">Name</small>
                  <div className="fw-semibold">
                    {campaignData?.campaign_name}
                  </div>
                </Col>
                <Col md={3}>
                  <small className="text-muted">Scheduled On</small>
                  <div>
                    {moment(new Date(campaignData?.start_date)).format(
                      "DD-MM-YYYY h:mm:ss A",
                    )}
                  </div>
                </Col>
                <Col md={3}>
                  <small className="text-muted">Template</small>
                  <div>
                    {campaignData?.template_name}{" "}
                    <i
                      className="fa fa-eye ms-2 text-muted"
                      style={{ cursor: "pointer" }}
                      onClick={handleShowModal}
                    ></i>{" "}
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-secondary small mb-1 d-flex align-items-center gap-1">
                    <i className="bi bi-bar-chart-steps"></i> Status
                  </div>
                  <div>
                    <span
                      className={`badge px-3 pb-2 rounded-pill bg-${getStatusColor(
                        campaignData?.campaign_status,
                      )}`}
                    >
                      <i className="bi bi-check-all me-1"></i>{" "}
                      {campaignData?.campaign_status}
                    </span>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
        <Row className="mx-5 mb-5">
          <Col>
            <Card className="p-0 bg-white shadow-sm border-0 rounded-3">
              <div className="px-4 pt-4 pb-0">
                <h6 className="mb-4">📬 Message Status Overview & History</h6>

                {/* Tabs */}
                <Row className="gx-0">
                  {["total", "delivered", "read", "clicked", "failed"].map(
                    (key) => {
                      if (
                        key === "clicked" &&
                        !template?.components?.find((c) => c.type === "BUTTONS")
                      ) {
                        return null;
                      }

                      const label = key.charAt(0).toUpperCase() + key.slice(1);
                      const count = campaignData?.[`${key}`] || 0;
                      // const percent = campaignData?.[`${key}_percentage`] || "0";
                      const percent =
                        key === "total"
                          ? campaignData?.sent_percentage || "0"
                          : campaignData?.[`${key}_percentage`] || "0";

                      const iconMap = {
                        total: "fa fa-list",
                        delivered: "fa fa-check-double text-muted",
                        read: "fa fa-check-double text-primary",
                        clicked: "fa fa-hand-pointer text-primary",
                        failed: "fa fa-exclamation-triangle text-danger",
                      };

                      const iconClass = iconMap[key] || "fa fa-info-circle";

                      return (
                        <Col key={key}>
                          <div
                            className={`text-center py-3 fw-semibold ${
                              selectedStatus === key
                                ? "tab-purple-active"
                                : "text-muted border-bottom border-2 border-light"
                            }`}
                            style={{
                              cursor: "pointer",
                              transition: "all 0.3s",
                            }}
                            onClick={() => handleStatusClick(key)}
                          >
                            <i
                              className={iconClass}
                              aria-hidden="true"
                              style={{ marginRight: 5 }}
                            ></i>
                            {label} ({count}){" "}
                            <div className="small">{percent}%</div>
                          </div>
                        </Col>
                      );
                    },
                  )}
                </Row>
              </div>

              {/* Table */}
              <div className="p-4 pt-3">
                <DatatableWrapper
                  body={filtered}
                  headers={header}
                  paginationOptionsProps={{
                    initialState: { rowsPerPage: 10, options: [5, 10, 15, 20] },
                  }}
                >
                  <Row className="mb-2 d-flex justify-content-start">
                    <Col
                      lg={3}
                      sm={6}
                      xs={6}
                      className="d-flex flex-col justify-content-start align-items-start"
                    >
                      <Filter />
                    </Col>
                    <Col
                      lg={5}
                      sm={6}
                      xs={6}
                      className="d-flex flex-col justify-content-start align-items-start"
                    ></Col>
                    <Col
                      lg={4}
                      sm={6}
                      xs={6}
                      className="d-flex flex-col justify-content-end align-items-end"
                    >
                      {canExport && (
                        <Button
                          className="btn btn-sm"
                          disabled={filtered.length === 0}
                          variant="outline-secondary"
                          onClick={handleDownload}
                        >
                          Download
                        </Button>
                      )}
                    </Col>
                  </Row>
                  <Table
                    striped
                    bordered
                    className="related-list-table"
                    responsive="sm"
                  >
                    <TableHeader />
                    <TableBody />
                  </Table>

                  <Row className="mb-2">
                    <Col lg={6} sm={12} xs={12}>
                      <Pagination />
                    </Col>
                  </Row>
                </DatatableWrapper>
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        animation={false}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Template Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Render your template preview here */}
          <div className="whatsapp-background w-100">
            <div className="template-box">
              {getHeader()}
              <div className="template-content mt-3">
                {renderFormattedMessage()}
                {getFooter()}
                {getButtons()}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <ToastContainer autoClose={30000} />
    </>
  );
};

export default CampaignTracking;
